import { User } from "@/pages/Public/pages/Login/schema/login";

import { Sender } from "../Sender";

import {
	CampaignStepChannels,
	EmailMode,
	LinkedinTier,
	SenderUserRow,
	UnmetRequirement,
	UserCapabilities,
} from "./senderSelector.types";

/** Flow node types (saved in `campaignConfig[uuid].initialNodes`) that need a LinkedIn account. */
const LINKEDIN_NODE_TYPES = new Set([
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinVisitNode",
	"linkedinRemoveInviteNode",
]);

/**
 * Regroup the flat, channel-level `Sender[]` into one row per sending user, in the order the users
 * are provided. Users without any sender are dropped (nothing to configure for them).
 */
export const groupSendersByUser = (senders: Sender[], users: User[]): SenderUserRow[] =>
	users.reduce<SenderUserRow[]>((rows, user) => {
		const userSenders = senders.filter((sender) => sender.userId === user.id);

		if (userSenders.length === 0) {
			return rows;
		}

		const emailSenders = userSenders.filter((sender) => sender.type === "email");

		rows.push({
			userId: user.id,
			user,
			name: `${user.firstName} ${user.lastName}`.trim(),
			linkedinSender: userSenders.find((sender) => sender.type === "linkedin"),
			emailSenders,
			primaryEmails: emailSenders.filter((sender) => sender.emailType === "primary"),
			secondaryEmails: emailSenders.filter((sender) => sender.emailType === "secondary"),
		});

		return rows;
	}, []);

/**
 * What a row's user can do. Email/LinkedIn presence comes from the senders that exist for them;
 * Sales Navigator is not a sender kind, so it is read from the user flags (stored `hasLiSn`, or the
 * live `unipile.salesNavigatorIsLoggedIn` when present).
 */
export const getUserCapabilities = (row: SenderUserRow): UserCapabilities => ({
	hasEmail: row.emailSenders.length > 0,
	hasLinkedIn: !!row.linkedinSender,
	hasSalesNav: !!(row.user.hasLiSn || row.user.unipile?.salesNavigatorIsLoggedIn),
	hasPremium: !!row.user.hasLinkedinPremium,
});

/** LinkedIn tier badge for a row (Sales Nav > Premium > plain connected > not connected). */
export const resolveLinkedinTier = (row: SenderUserRow): LinkedinTier => {
	const caps = getUserCapabilities(row);

	if (!caps.hasLinkedIn) {
		return "none";
	}

	if (caps.hasSalesNav) {
		return "salesnav";
	}

	if (row.user.hasLinkedinPremium) {
		return "premium";
	}

	return "connected";
};

/**
 * Which channels a campaign's saved sequence needs. An InMail node requires a Sales Navigator
 * license; the other LinkedIn nodes only need a connected LinkedIn account; an email node needs a
 * connected email. Returns all-false for an empty/absent sequence.
 */
export const getCampaignStepChannels = (initialNodes: unknown): CampaignStepChannels => {
	const nodes = Array.isArray(initialNodes) ? initialNodes : [];
	const channels: CampaignStepChannels = { email: false, linkedin: false, salesnav: false };

	for (const node of nodes) {
		const type = (node as { type?: string } | null)?.type;

		if (type === "emailNode") {
			channels.email = true;
		} else if (type === "linkedinInmailNode") {
			channels.salesnav = true;
		} else if (type && LINKEDIN_NODE_TYPES.has(type)) {
			channels.linkedin = true;
		}
	}

	return channels;
};

/**
 * The channel requirements a user does NOT meet for the campaign's current steps. Empty when the
 * campaign has no steps yet (`steps` undefined) or the user can perform every step it contains.
 */
export const getUnmetRequirements = (
	caps: UserCapabilities,
	steps?: CampaignStepChannels
): UnmetRequirement[] => {
	if (!steps) {
		return [];
	}

	const unmet: UnmetRequirement[] = [];

	if (steps.email && !caps.hasEmail) {
		unmet.push({ channel: "email", label: "a connected email", stepLabel: "an email step" });
	}

	if (steps.linkedin && !caps.hasLinkedIn) {
		unmet.push({
			channel: "linkedin",
			label: "a connected LinkedIn account",
			stepLabel: "a LinkedIn step",
		});
	}

	// An InMail step needs a connected LinkedIn account AND a paid tier (Premium or Sales Navigator).
	if (steps.salesnav && !(caps.hasLinkedIn && (caps.hasPremium || caps.hasSalesNav))) {
		unmet.push({
			channel: "salesnav",
			label: "a Premium or Sales Navigator LinkedIn account",
			stepLabel: "a LinkedIn InMail step",
		});
	}

	return unmet;
};

/** The "This campaign requires" wording of each channel a step can need. */
const REQUIREMENT_LABELS: Record<keyof CampaignStepChannels, string> = {
	email: "a connected email",
	linkedin: "a connected LinkedIn account",
	salesnav: "a Premium or Sales Navigator LinkedIn account",
};

/** The channels a campaign's steps need, in display order. */
const requiredChannels = (steps?: CampaignStepChannels): (keyof CampaignStepChannels)[] =>
	steps ? (["email", "linkedin", "salesnav"] as const).filter((channel) => steps[channel]) : [];

/**
 * Human-readable list of what a campaign's steps require from each sender — used for the
 * "Campaign requires …" hint above the senders table. Empty when there are no channel-bound steps.
 */
export const describeCampaignRequirements = (steps?: CampaignStepChannels): string[] =>
	requiredChannels(steps).map((channel) => REQUIREMENT_LABELS[channel]);

/** A user is eligible for the campaign when it has no unmet step requirements. */
export const isUserEligible = (caps: UserCapabilities, steps?: CampaignStepChannels): boolean =>
	getUnmetRequirements(caps, steps).length === 0;

/**
 * Whether a user participates in the campaign — i.e. has at least one active channel. It says
 * nothing about LinkedIn: a row ticked by its mailboxes alone does not cover LinkedIn steps
 * (PREC-4324). Ask {@link isLinkedinActive} for that.
 */
export const isUserSelected = (row: SenderUserRow): boolean =>
	!!row.linkedinSender?.active || row.emailSenders.some((sender) => sender.active);

/**
 * Whether the user's LinkedIn sender is switched on for this campaign. The tier badge, the row's
 * LinkedIn toggle and the "a connected LinkedIn account" requirement all read this, so no part of
 * the row can say LinkedIn is covered while the stored sender is off (PREC-4324).
 */
export const isLinkedinActive = (row: SenderUserRow): boolean => !!row.linkedinSender?.active;

/** Switch one user's LinkedIn sender on or off. Their mailboxes are left as they are. */
export const toggleLinkedinActive = (senders: Sender[], userId: number): Sender[] =>
	senders.map((sender) =>
		sender.userId === userId && sender.type === "linkedin"
			? { ...sender, active: !sender.active }
			: sender
	);

/** One "This campaign requires" entry, and whether the active senders cover it. */
export interface CampaignRequirementStatus {
	label: string;
	met: boolean;
}

/**
 * The campaign's channel requirements, each marked met only when an active sender covers it: an
 * active mailbox for an email step, an active LinkedIn sender for a LinkedIn step, and an active
 * LinkedIn sender with Premium or Sales Navigator for an InMail step. Mirrors the builder's
 * validator (`collectLinkedinSenderErrors`), which is what refuses the campaign.
 */
export const describeRequirementStatus = (
	rows: SenderUserRow[],
	steps?: CampaignStepChannels
): CampaignRequirementStatus[] => {
	const linkedinRows = rows.filter(isLinkedinActive);
	const hasActiveEmail = rows.some((row) => row.emailSenders.some((sender) => sender.active));
	const hasPaidLinkedin = linkedinRows.some((row) => {
		const caps = getUserCapabilities(row);

		return caps.hasPremium || caps.hasSalesNav;
	});

	const met: Record<keyof CampaignStepChannels, boolean> = {
		email: hasActiveEmail,
		linkedin: linkedinRows.length > 0,
		salesnav: hasPaidLinkedin,
	};

	return requiredChannels(steps).map((channel) => ({
		label: REQUIREMENT_LABELS[channel],
		met: met[channel],
	}));
};

/**
 * Which email-mode segment a row currently reflects. `custom` when the active inboxes don't map
 * cleanly onto primary-only / secondary-only / both (e.g. a partial secondary selection).
 */
export const deriveEmailMode = (row: SenderUserRow): EmailMode => {
	const totalPrimary = row.primaryEmails.length;
	const totalSecondary = row.secondaryEmails.length;
	const activePrimary = row.primaryEmails.filter((sender) => sender.active).length;
	const activeSecondary = row.secondaryEmails.filter((sender) => sender.active).length;

	if (totalPrimary > 0 && activePrimary === totalPrimary && activeSecondary === 0) {
		return "primary";
	}

	if (totalSecondary > 0 && activeSecondary === totalSecondary && activePrimary === 0) {
		return "secondary";
	}

	if (
		totalPrimary > 0 &&
		totalSecondary > 0 &&
		activePrimary === totalPrimary &&
		activeSecondary === totalSecondary
	) {
		return "both";
	}

	return "custom";
};

/** Bulk-set a single user's email senders to match an email mode. LinkedIn is left untouched. */
export const applyEmailModeToUser = (
	senders: Sender[],
	userId: number,
	mode: Exclude<EmailMode, "custom">
): Sender[] =>
	senders.map((sender) => {
		if (sender.userId !== userId || sender.type !== "email") {
			return sender;
		}

		const isPrimary = sender.emailType === "primary";
		const active = mode === "both" ? true : mode === "primary" ? isPrimary : !isPrimary;

		return { ...sender, active };
	});

/** Toggle one inbox on/off by its uuid (primary included — it is freely de-selectable). */
export const toggleEmailActive = (senders: Sender[], uuid: string): Sender[] =>
	senders.map((sender) => (sender.uuid === uuid ? { ...sender, active: !sender.active } : sender));

/**
 * Include or exclude a whole user. Including activates their LinkedIn account (if any) plus a
 * single email — the primary, or the first available email when there is no primary — so a
 * "selected" user always has at least one active channel. Excluding deactivates everything.
 */
export const setUserSelected = (senders: Sender[], userId: number, select: boolean): Sender[] => {
	if (!select) {
		return senders.map((sender) =>
			sender.userId === userId ? { ...sender, active: false } : sender
		);
	}

	const userEmails = senders.filter(
		(sender) => sender.userId === userId && sender.type === "email"
	);
	const emailToActivate =
		userEmails.find((sender) => sender.emailType === "primary")?.uuid ?? userEmails[0]?.uuid;

	return senders.map((sender) => {
		if (sender.userId !== userId) {
			return sender;
		}

		if (sender.type === "linkedin") {
			return { ...sender, active: true };
		}

		return { ...sender, active: sender.uuid === emailToActivate };
	});
};

/** Include/exclude every eligible user at once (the master select-all checkbox). */
export const setAllUsersSelected = (
	senders: Sender[],
	eligibleUserIds: Set<number>,
	select: boolean
): Sender[] => {
	let next = senders;

	eligibleUserIds.forEach((userId) => {
		next = setUserSelected(next, userId, select);
	});

	return next;
};

/** Apply one email mode to every eligible, selected, email-capable user (the master switch). */
export const applyMasterEmailMode = (
	senders: Sender[],
	rows: SenderUserRow[],
	targetUserIds: Set<number>,
	mode: Exclude<EmailMode, "custom">
): Sender[] => {
	let next = senders;

	rows.forEach((row) => {
		if (targetUserIds.has(row.userId) && row.emailSenders.length > 0) {
			next = applyEmailModeToUser(next, row.userId, mode);
		}
	});

	return next;
};

/** The mode the master switch should show: a shared value across selected users, or `custom`. */
export const deriveMasterEmailMode = (rows: SenderUserRow[]): EmailMode => {
	const modes = new Set(
		rows.filter((row) => row.emailSenders.length > 0).map((row) => deriveEmailMode(row))
	);

	if (modes.size === 0) {
		return "primary";
	}

	return modes.size === 1 ? ([...modes][0] as EmailMode) : "custom";
};
